/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import Link from 'next/link';
import TraceIDProvider from '@/providers/TraceIdProvider';
import { handleCopyLink } from '@/utils/handleCopyLink';
import Icons from '@/ux-common/src/icons/icons';
import { kahuCopyInfoType } from 'types/kahu';

import { KahuTraceIdTooltip } from './kahu-trace-id-tooltip';

type KahuResponseLinkProps = {
  text: string;
  href: string;
  occurrenceKey: string;
  copiedInfo: kahuCopyInfoType | null;
  setCopiedInfo: React.Dispatch<React.SetStateAction<kahuCopyInfoType | null>>;
};

export const KahuResponseLink = ({
  text,
  href,
  occurrenceKey,
  copiedInfo,
  setCopiedInfo,
}: KahuResponseLinkProps) => {
  const url = new URL(href, window.location.origin);
  const appName = url.pathname.match(/\/apps\/([^/]+)\//)?.[1] ?? '';

  const isCopied = copiedInfo?.occurrenceKey === occurrenceKey;

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    handleCopyLink(
      e,
      text,
      href,
      occurrenceKey,
      appName ? 'appName' : 'workflowName',
      setCopiedInfo
    );
  };

  if (!href.includes('trace_id')) {
    return (
      <span className="group relative inline-block">
        <Link
          href={href}
          className="rounded bg-[#FFFFFF14] px-[3px] text-body font-normal text-body-subtle hover:text-[#fff]"
        >
          {text}
        </Link>

        <button
          aria-label="Copy value"
          className={`absolute right-0 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded bg-[#08242F] transition-opacity ${
            isCopied
              ? 'pointer-events-auto opacity-100'
              : 'pointer-events-none opacity-0 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100'
          }`}
          onClick={handleCopy}
        >
          {isCopied ? Icons.CopyGreenCheckIcon() : Icons.CopyIcon()}
        </button>
      </span>
    );
  }

  return (
    <TraceIDProvider appName={appName}>
      <KahuTraceIdTooltip
        traceId={text}
        href={href}
        occurrenceKey={occurrenceKey}
        copiedInfo={copiedInfo}
        setCopiedInfo={setCopiedInfo}
      />
    </TraceIDProvider>
  );
};
